import React, { useState, useEffect, useCallback, useContext } from 'react';
import PropTypes from 'prop-types';
import {
    Modal,
    ModalHeader,
    ModalBody,
    ModalFooter,
    Button,
    ButtonSecondary,
    HorizontalFlex,
    VerticalFlex,
    Input,
} from 'smartsuite-ui';
import ShareUrlCopyAlert from './ShareUrlCopyAlert';
import {
    fallbackCopyTextToClipboard,
    copyTextToClipboard,
} from './copyToClipboardUtil';
import { useLabelContext } from '../../shared/hooks/labelContext';
import { getShareInfo, shareDashboardLink } from '../dashboardLinkUtils';
import DashboardContext from '../../shared/hooks/dashboardContext';

export default function ShareModal(props) {
    const { dashboardLabels, translationLabels } = useLabelContext();
    const { uriConstants } = useContext(DashboardContext);

    const [shareableLink, setShareableLink] = useState('');
    const [isCopyingSuccessful, setIsCopyingSuccessful] = useState(false);
    const [hasCopyBeenClicked, setHasCopyBeenClicked] = useState(false);

    const onSendToClipboard = useCallback(async (link) => {
        const elementToCopyFrom = document.querySelector('#copyInput');
        elementToCopyFrom.focus();
        elementToCopyFrom.select();

        if (!navigator.clipboard) {
            const result = fallbackCopyTextToClipboard(elementToCopyFrom);
            setIsCopyingSuccessful(result);
            setHasCopyBeenClicked(true);
            return;
        }

        const navigatorResult = await copyTextToClipboard(link);
        setIsCopyingSuccessful(navigatorResult);
        setHasCopyBeenClicked(true);
    }, []);

    useEffect(() => {
        const { id, link } = getShareInfo(
            props.viewKey,
            props.dashboardKey,
            uriConstants.getShareableUri
        );

        shareDashboardLink(
            id,
            props.configurationForShare,
            uriConstants.saveSharedUrl
        )
            .then(
                () => setShareableLink(link),
                (error) => console.log(error) // eslint-disable-line no-console
            )
            .then(() => onSendToClipboard(link));
    }, [
        props.viewKey,
        props.configurationForShare,
        uriConstants,
        props.dashboardKey,
        onSendToClipboard,
    ]);

    /* eslint-disable react/no-unknown-property */
    return (
        <Modal onOverlayClick={props.onDismiss}>
            <ModalHeader onDismiss={props.onDismiss}>
                {dashboardLabels.shareDashboard}
            </ModalHeader>
            <ModalBody>
                <VerticalFlex>
                    <HorizontalFlex cssClassModifiers={['align-flex-end']}>
                        <Input
                            id="copyInput"
                            cssClassModifiers={['small-relative']}
                            value={shareableLink}
                            contentEditable="true"
                            label={dashboardLabels.shareableLink}
                            onChange={() => {}}
                        />
                        <Button
                            onClick={() => onSendToClipboard(shareableLink)}
                        >
                            {translationLabels.copy}
                        </Button>
                    </HorizontalFlex>
                    <ShareUrlCopyAlert
                        hasCopyBeenClicked={hasCopyBeenClicked}
                        isCopyingSuccessful={isCopyingSuccessful}
                        unappliedFiltersExist={props.unappliedFiltersExist}
                    />
                </VerticalFlex>
            </ModalBody>
            <ModalFooter>
                <HorizontalFlex cssClassModifiers={['justify-flex-end']}>
                    <ButtonSecondary onClick={props.onDismiss}>
                        {translationLabels.close}
                    </ButtonSecondary>
                </HorizontalFlex>
            </ModalFooter>
        </Modal>
    );
    /* eslint-disable react/no-unknown-property */
}

ShareModal.propTypes = {
    viewKey: PropTypes.string.isRequired,
    configurationForShare: PropTypes.instanceOf(Object).isRequired,
    dashboardKey: PropTypes.string.isRequired,
    onDismiss: PropTypes.func.isRequired,
    unappliedFiltersExist: PropTypes.bool.isRequired,
};
